import { memo, SyntheticEvent, useContext, useState } from 'react'
import { Box, Flex, Icon } from '@stoplight/mosaic'

import { ActiveIdContext } from './TableOfContents'
import { TableOfContentsItem } from './TableOfContentsItem'
import { TableOfContentsNode } from './TableOfContentsNode'
import { TableOfContentsGroupItem } from './TableOfContentsGroupItem'
import {
  isNodeGroup,
  isGroupOpenByDefault,
  TableOfContentsNodeGroup,
  TableOfContentsGroup as Group,
} from './index'

type TableOfContentsGroupProps = {
  depth: number
  item: Group | TableOfContentsNodeGroup
  maxDepthOpenByDefault?: number
  onLinkClick?: () => void
}

export const TableOfContentsGroup = memo<TableOfContentsGroupProps>(
  ({ depth, item, maxDepthOpenByDefault, onLinkClick = () => {} }) => {
    const activeId = useContext(ActiveIdContext)
    const [isOpen, setIsOpen] = useState(() => {
      // Only need to check during initial render
      return isGroupOpenByDefault(depth, item, activeId, maxDepthOpenByDefault)
    })

    const handleClick = (e: SyntheticEvent, forceOpen?: boolean) => {
      setIsOpen(forceOpen ? true : !isOpen)
    }

    const meta = (
      <Flex alignItems="center">
        <Box
          as={Icon}
          icon={['fas', isOpen ? 'chevron-down' : 'chevron-right']}
          color="muted"
          fixedWidth
          onClick={(e: SyntheticEvent) => {
            // Don't propagate event when clicking icon
            e.stopPropagation()
            e.preventDefault()
            handleClick(e)
          }}
        />
      </Flex>
    )

    const elem = isNodeGroup(item) ? (
      <TableOfContentsNode
        depth={depth}
        item={item}
        meta={meta}
        onClick={handleClick}
        onLinkClick={onLinkClick}
      />
    ) : (
      <TableOfContentsItem
        title={item.title}
        meta={meta}
        onClick={handleClick}
        depth={depth}
      />
    )

    return (
      <>
        {elem}
        {isOpen &&
          item.items.map((groupItem, key) => {
            return (
              <TableOfContentsGroupItem
                key={key}
                item={groupItem}
                depth={depth + 1}
                onLinkClick={onLinkClick}
              />
            )
          })}
      </>
    )
  }
)
